import { useEffect, useState } from 'react';
import { useEditorStore } from '../stores/editorStore';
import type { Hotspot } from '../types';
import { fileToDataUrl } from '../utils/fileUtils';
import { EmojiPicker } from '@tl4k/admin-ui';

function PropertyPanel() {
  const { project, selectedElementId, updateElement } = useEditorStore();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const selectedElement = project?.elements.find((el) => el.id === selectedElementId);
  const isHotspot = selectedElement?.type === 'hotspot';
  const hotspot = selectedElement as Hotspot | undefined;
  // ... (keep existing)

  // ... (inside return)



  // Debug log
  useEffect(() => {
    console.log('PropertyPanel updated:', {
      hasProject: !!project,
      projectId: project?.id,
      elementsCount: project?.elements.length,
      selectedElementId,
      foundElement: !!selectedElement,
      elementIds: project?.elements.map(e => e.id)
    });
  }, [project, selectedElementId, selectedElement]);

  if (!selectedElementId) {
    return (
      <div className="p-4">
        <h2 className="font-medium text-gray-800 mb-2">Proprietà</h2>
        <div className="text-center py-8 text-gray-400 text-sm">
          <p>Seleziona un elemento</p>
          <p className="mt-2">per modificare le sue proprietà</p>
          {project && (
            <p className="mt-2 text-xs text-gray-300">
              Elementi disponibili: {project.elements.length}
            </p>
          )}
        </div>

        {/* Project info */}
        {project && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Informazioni progetto</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Nome</span>
                <span className="text-gray-800">{project.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Dimensioni</span>
                <span className="text-gray-800">{project.width} × {project.height}px</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Elementi</span>
                <span className="text-gray-800">{project.elements.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Creato</span>
                <span className="text-gray-800">{project.createdAt.toLocaleDateString('it')}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!isHotspot || !hotspot) {
    return (
      <div className="p-4">
        <h2 className="font-medium text-gray-800 mb-2">Proprietà</h2>
        <p className="text-sm text-gray-500">Tipo di elemento non supportato per l'editing</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="font-medium text-gray-800 mb-4">Proprietà Hotspot</h2>

      <div className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titolo
          </label>
          <input
            type="text"
            value={hotspot.title || ''}
            onChange={(e) => updateElement(hotspot.id, { title: e.target.value })}
            placeholder="Titolo dell'hotspot"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Content (Rich text area) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contenuto
          </label>
          <textarea
            value={hotspot.content}
            onChange={(e) => updateElement(hotspot.id, { content: e.target.value })}
            placeholder="Scrivi il contenuto dell'hotspot..."
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
          />
          <p className="text-xs text-gray-400 mt-1">
            Supporta testo semplice. Prossimamente: formattazione HTML.
          </p>
        </div>

        {/* Appearance */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Aspetto</h3>

          {/* Trigger Mechanism */}
          <div className="flex items-center justify-between mb-4">
            <label className="text-sm text-gray-700" title="Quando mostrare il contenuto">Attivazione</label>
            <select
              value={hotspot.trigger || 'click'}
              onChange={(e) => updateElement(hotspot.id, { trigger: e.target.value as 'click' | 'hover' })}
              className="px-2 py-1 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-500"
            >
              <option value="click">Click</option>
              <option value="hover">Mouse Over</option>
            </select>
          </div>

          {/* Visibility Toggle */}
          <div className="flex items-center justify-between mb-4">
            <label className="text-sm text-gray-700">Visibile</label>
            <button
              onClick={() => updateElement(hotspot.id, { isVisible: !hotspot.isVisible })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hotspot.isVisible ? 'bg-primary-600' : 'bg-gray-200'
                }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hotspot.isVisible ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>

          {/* Icon Type Selection */}
          <div className="mb-2">
            <label className="block text-xs text-gray-500 mb-1">Tipo Icona</label>
            <select
              value={hotspot.iconType || 'default'}
              onChange={(e) => updateElement(hotspot.id, { iconType: e.target.value as any })}
              className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-500"
            >
              <option value="default">Default (+)</option>
              <option value="emoji">Emoji</option>
              <option value="image">Immagine (URL)</option>
            </select>
          </div>

          {/* Conditional Icon Inputs */}
          {hotspot.iconType === 'emoji' && (
            <div>
              <label className="block text-xs text-gray-500 mb-2">Emoji</label>

              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="flex-1 py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="text-xl">{hotspot.iconContent || '😀'}</span>
                  <span>{showEmojiPicker ? 'Chiudi lista' : 'Scegli emoji'}</span>
                </button>
              </div>

              {showEmojiPicker && (
                <div className="mb-4">
                  <EmojiPicker
                    onSelect={(emoji) => {
                      updateElement(hotspot.id, { iconContent: emoji });
                      setShowEmojiPicker(false);
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {hotspot.iconType === 'image' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">URL Immagine</label>
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  value={hotspot.iconContent || ''}
                  onChange={(e) => updateElement(hotspot.id, { iconContent: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                />
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="icon-upload"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const dataUrl = await fileToDataUrl(file);
                          updateElement(hotspot.id, { iconContent: dataUrl });
                        } catch (error) {
                          console.error('Error uploading icon:', error);
                        }
                      }
                    }}
                  />
                  <label
                    htmlFor="icon-upload"
                    className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-lg text-sm cursor-pointer transition-colors text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                    Carica Immagine
                  </label>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">Incolla URL o carica un'immagine dal tuo dispositivo</p>
            </div>
          )}
        </div>

        {/* Position info */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Posizione</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <label className="text-xs text-gray-500">X</label>
              <input
                type="number"
                value={Math.round(hotspot.position.x)}
                readOnly
                className="w-full px-2 py-1 bg-gray-50 border border-gray-200 rounded text-gray-600"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Y</label>
              <input
                type="number"
                value={Math.round(hotspot.position.y)}
                readOnly
                className="w-full px-2 py-1 bg-gray-50 border border-gray-200 rounded text-gray-600"
              />
            </div>
          </div>
        </div>

        {/* Bounding box info */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Dimensioni</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <label className="text-xs text-gray-500">Larghezza</label>
              <input
                type="number"
                value={Math.round(hotspot.boundingBox.width)}
                readOnly
                className="w-full px-2 py-1 bg-gray-50 border border-gray-200 rounded text-gray-600"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Altezza</label>
              <input
                type="number"
                value={Math.round(hotspot.boundingBox.height)}
                readOnly
                className="w-full px-2 py-1 bg-gray-50 border border-gray-200 rounded text-gray-600"
              />
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="border-t pt-4 text-xs text-gray-400">
          <p>Creato: {hotspot.createdAt.toLocaleString('it')}</p>
          <p>Modificato: {hotspot.updatedAt.toLocaleString('it')}</p>
        </div>
      </div>
    </div>
  );
}

export default PropertyPanel;
